/**
 * Advisor Rules Tab — כללי יועצי
 * Manage dynamic AI rules that get injected into the system prompt at runtime.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { RuleScope } from '../../config/tracks'
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react'
import api from '../../services/api'

interface AdvisorRule {
  _id: string
  category: string
  title: string
  content: string
  priority: 'mandatory' | 'recommended' | 'note'
  isActive: boolean
  createdAt: string
}

const CATEGORIES = [
  { value: 'investments', label: 'השקעות' },
  { value: 'pension', label: 'פנסיוני' },
  { value: 'tax', label: 'מיסים' },
  { value: 'behavior', label: 'התנהגות' },
  { value: 'general', label: 'כללי' },
]

const PRIORITIES = [
  { value: 'mandatory', label: 'חובה', color: 'text-red-600 bg-red-50' },
  { value: 'recommended', label: 'מומלץ', color: 'text-blue-600 bg-blue-50' },
  { value: 'note', label: 'הערה', color: 'text-gray-600 bg-gray-50' },
]

// Must match the backend AdvisorRule model maxlength for `content`
const CONTENT_MAX_LENGTH = 10000
const TITLE_MAX_LENGTH = 200

interface AdvisorRulesTabProps {
  /**
   * Which AI persona these rules steer. Defaults to the advisory ("יועצי")
   * product, so the existing כללי יועצי tab shows exactly what it always did.
   */
  scope?: RuleScope
}

export default function AdvisorRulesTab({ scope = 'advisory' }: AdvisorRulesTabProps) {
  const [allRules, setAllRules] = useState<AdvisorRule[]>([])
  const [loading, setLoading] = useState(true)
  const [filterCategory, setFilterCategory] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'disabled'>('all')
  const [showForm, setShowForm] = useState(false)
  const [editingRule, setEditingRule] = useState<AdvisorRule | null>(null)

  // Form state
  const [formCategory, setFormCategory] = useState('general')
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formPriority, setFormPriority] = useState<string>('recommended')
  const [formError, setFormError] = useState('')

  // Single source of truth — full list from the server. Filtering happens
  // client-side so all filters (category/priority/status) apply instantly and
  // stay consistent with the summary breakdown ("במבט על").
  const fetchAllRules = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/advisor-rules', { params: { scope } })
      setAllRules(res.data?.rules || [])
    } catch {
      setAllRules([])
    } finally {
      setLoading(false)
    }
  }, [scope])

  const refresh = fetchAllRules

  useEffect(() => { fetchAllRules() }, [fetchAllRules])

  const hasActiveFilter = !!filterCategory || !!filterPriority || filterStatus !== 'all'

  const rules = useMemo(() => allRules.filter(r => {
    if (filterCategory && r.category !== filterCategory) return false
    if (filterPriority && r.priority !== filterPriority) return false
    if (filterStatus === 'active' && !r.isActive) return false
    if (filterStatus === 'disabled' && r.isActive) return false
    return true
  }), [allRules, filterCategory, filterPriority, filterStatus])

  const resetForm = () => {
    setFormCategory('general')
    setFormTitle('')
    setFormContent('')
    setFormPriority('recommended')
    setFormError('')
    setEditingRule(null)
    setShowForm(false)
  }

  const handleEdit = (rule: AdvisorRule) => {
    setEditingRule(rule)
    setFormCategory(rule.category)
    setFormTitle(rule.title)
    setFormContent(rule.content)
    setFormPriority(rule.priority)
    setFormError('')
    setShowForm(true)
  }

  const handleSave = async () => {
    if (!formTitle.trim() || !formContent.trim()) return
    setFormError('')
    if (formContent.length > CONTENT_MAX_LENGTH) {
      setFormError(`תוכן הכלל ארוך מדי — עד ${CONTENT_MAX_LENGTH.toLocaleString('he-IL')} תווים (הוזנו ${formContent.length.toLocaleString('he-IL')})`)
      return
    }

    try {
      if (editingRule) {
        await api.put(`/admin/advisor-rules/${editingRule._id}`, {
          category: formCategory, title: formTitle, content: formContent, priority: formPriority,
        })
      } else {
        await api.post('/admin/advisor-rules', {
          scope,
          category: formCategory, title: formTitle, content: formContent, priority: formPriority,
        })
      }
      resetForm()
      refresh()
    } catch (err: any) {
      console.error('Failed to save rule:', err)
      setFormError(err?.response?.data?.message || 'שגיאה בשמירת הכלל. נסה שוב.')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('למחוק את הכלל?')) return
    try {
      await api.delete(`/admin/advisor-rules/${id}`)
      refresh()
    } catch (err) {
      console.error('Failed to delete rule:', err)
    }
  }

  const handleToggle = async (rule: AdvisorRule) => {
    try {
      await api.put(`/admin/advisor-rules/${rule._id}`, { isActive: !rule.isActive })
      refresh()
    } catch (err) {
      console.error('Failed to toggle rule:', err)
    }
  }

  const getCategoryLabel = (cat: string) => CATEGORIES.find(c => c.value === cat)?.label || cat
  const getPriorityInfo = (p: string) => PRIORITIES.find(pr => pr.value === p) || PRIORITIES[1]

  // Summary breakdown ("פילוח") — always over the full system, not the current filter
  const total = allRules.length
  const activeCount = allRules.filter(r => r.isActive).length
  const disabledCount = total - activeCount
  const countByCategory = (cat: string) => allRules.filter(r => r.category === cat).length
  const countByPriority = (p: string) => allRules.filter(r => r.priority === p).length

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-bold text-gray-900">כללי יועצי</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            כללים שנוספים אוטומטית להנחיות ה-AI בכל שיחה
          </p>
        </div>
        <div className="flex items-center flex-wrap gap-2 justify-end">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            aria-label="סינון כללים לפי קטגוריה"
            className="input text-sm py-1.5"
          >
            <option value="">כל הקטגוריות</option>
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            aria-label="סינון כללים לפי עדיפות"
            className="input text-sm py-1.5"
          >
            <option value="">כל העדיפויות</option>
            {PRIORITIES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'disabled')}
            aria-label="סינון כללים לפי סטטוס"
            className="input text-sm py-1.5"
          >
            <option value="all">פעילים ומושבתים</option>
            <option value="active">כללים פעילים</option>
            <option value="disabled">כללים מושבתים</option>
          </select>
          <button onClick={refresh} className="btn btn-outline text-sm p-2" aria-label="רענן">
            <RefreshCw size={16} aria-hidden="true" />
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true) }}
            className="btn btn-primary text-sm"
          >
            <Plus size={16} aria-hidden="true" />
            כלל חדש
          </button>
        </div>
      </div>

      {/* Summary breakdown — פילוח כללים במבט על */}
      {total > 0 && (
        <div className="card mb-6 bg-gray-50/60">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {/* Total */}
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-gray-900">{total}</span>
              <div className="text-sm text-gray-500 leading-tight">
                <div>סה״כ כללים</div>
                <div className="text-xs text-gray-400">
                  {activeCount} פעילים
                  {disabledCount > 0 && ` · ${disabledCount} מושבתים`}
                </div>
              </div>
            </div>

            <div className="h-8 w-px bg-gray-200 hidden sm:block" />

            {/* By priority — לפי עדיפות */}
            <div>
              <div className="text-xs text-gray-400 mb-1">לפי עדיפות</div>
              <div className="flex flex-wrap gap-1.5">
                {PRIORITIES.map(p => (
                  <span key={p.value} className={`text-xs font-medium px-2 py-0.5 rounded-full ${p.color}`}>
                    {p.label} {countByPriority(p.value)}
                  </span>
                ))}
              </div>
            </div>

            <div className="h-8 w-px bg-gray-200 hidden sm:block" />

            {/* By category — לפי קטגוריה */}
            <div>
              <div className="text-xs text-gray-400 mb-1">לפי קטגוריה</div>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map(c => (
                  <span key={c.value} className="text-xs font-medium px-2 py-0.5 rounded-full text-gray-600 bg-gray-100">
                    {c.label} {countByCategory(c.value)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card mb-6 border-2 border-primary-200 bg-primary-50/30">
          <h3 className="font-bold text-gray-900 mb-4">
            {editingRule ? 'עריכת כלל' : 'כלל חדש'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="advisor-rule-category" className="block text-sm font-medium text-gray-700 mb-1">קטגוריה</label>
              <select id="advisor-rule-category" value={formCategory} onChange={e => setFormCategory(e.target.value)} className="input">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="advisor-rule-priority" className="block text-sm font-medium text-gray-700 mb-1">עדיפות</label>
              <select id="advisor-rule-priority" value={formPriority} onChange={e => setFormPriority(e.target.value)} className="input">
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="advisor-rule-title" className="block text-sm font-medium text-gray-700 mb-1">כותרת</label>
              <input
                id="advisor-rule-title"
                value={formTitle}
                onChange={e => setFormTitle(e.target.value)}
                placeholder="כותרת קצרה..."
                className="input"
                maxLength={TITLE_MAX_LENGTH}
                style={{ fontSize: '16px' }}
              />
            </div>
          </div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="advisor-rule-content" className="block text-sm font-medium text-gray-700">תוכן הכלל</label>
              <span className={`text-xs ${formContent.length > CONTENT_MAX_LENGTH ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                {formContent.length.toLocaleString('he-IL')} / {CONTENT_MAX_LENGTH.toLocaleString('he-IL')} תווים
              </span>
            </div>
            <textarea
              id="advisor-rule-content"
              value={formContent}
              onChange={e => setFormContent(e.target.value)}
              placeholder="הכלל שיתווסף להנחיות ה-AI..."
              className="input w-full"
              rows={6}
              maxLength={CONTENT_MAX_LENGTH}
              style={{ fontSize: '16px' }}
            />
          </div>
          {formError && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
              {formError}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={handleSave} className="btn btn-primary text-sm" disabled={!formTitle.trim() || !formContent.trim()}>
              {editingRule ? 'עדכן' : 'שמור'}
            </button>
            <button onClick={resetForm} className="btn btn-outline text-sm">ביטול</button>
          </div>
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-gray-500">
            {hasActiveFilter ? 'אין כללים התואמים לסינון' : 'אין כללים עדיין'}
          </p>
          {hasActiveFilter && (
            <button
              onClick={() => { setFilterCategory(''); setFilterPriority(''); setFilterStatus('all') }}
              className="btn btn-outline text-sm mt-3"
            >
              נקה סינון
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => {
            const pri = getPriorityInfo(rule.priority)
            return (
              <div
                key={rule._id}
                className={`border rounded-xl p-4 ${rule.isActive ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pri.color}`}>
                        {pri.label}
                      </span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                        {getCategoryLabel(rule.category)}
                      </span>
                      {!rule.isActive && (
                        <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">מושבת</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">{rule.title}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{rule.content}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => handleToggle(rule)} className="p-1.5 hover:bg-gray-100 rounded" title={rule.isActive ? 'השבת' : 'הפעל'} aria-label={rule.isActive ? 'השבת' : 'הפעל'}>
                      {rule.isActive ? <ToggleRight size={20} className="text-green-500" aria-hidden="true" /> : <ToggleLeft size={20} className="text-gray-400" aria-hidden="true" />}
                    </button>
                    <button onClick={() => handleEdit(rule)} className="p-1.5 hover:bg-gray-100 rounded" title="ערוך" aria-label="ערוך">
                      <Edit2 size={16} className="text-gray-500" aria-hidden="true" />
                    </button>
                    <button onClick={() => handleDelete(rule._id)} className="p-1.5 hover:bg-red-50 rounded" title="מחק" aria-label="מחק">
                      <Trash2 size={16} className="text-red-400" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
